import { TrendingUp } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

/**
 * SalesChart Component - Enhanced Version
 * رسم بياني لمبيعات آخر 7 أيام باستخدام Recharts
 *
 * Protocols Applied:
 * - P-COMPONENT-001: Clean Component Structure
 * - P-RTL-001: RTL Support
 * - P-DARK-MODE-001: Dark Mode Support
 * - P-RESPONSIVE-001: Mobile First Design
 * - P017: Performance
 *
 * @param {Object} props
 * @param {Array} props.data - Sales data array
 * @param {boolean} props.loading - Loading state
 */

const SalesChart = ({ data = [], loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 md:p-6">
        <h2 className="text-base md:text-lg font-bold text-gray-900 dark:text-white mb-4">
          اتجاه المبيعات
        </h2>
        <div className="animate-pulse">
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 md:p-6">
        <h2 className="text-base md:text-lg font-bold text-gray-900 dark:text-white mb-4">
          اتجاه المبيعات
        </h2>
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          لا توجد بيانات لعرضها
        </div>
      </div>
    );
  }

  // Calculate statistics
  const totalSales = data.reduce((sum, d) => sum + d.sales, 0);
  const avgSales = totalSales / data.length;
  const maxSales = Math.max(...data.map(d => d.sales));

  // Format currency in KWD
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-KW', {
      style: 'currency',
      currency: 'KWD',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(amount);
  };

  // Format short date for mobile
  const formatShortDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ar-EG', { day: 'numeric', month: 'short' });
  };

  // Custom Tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-900 dark:bg-gray-700 text-white px-3 py-2 rounded-lg shadow-xl">
          <p className="text-xs font-semibold mb-1">{data.label}</p>
          <p className="text-sm font-bold text-blue-300">
            {formatCurrency(data.sales)}
          </p>
          <p className="text-xs text-gray-300">
            {data.count} فاتورة
          </p>
        </div>
      );
    }
    return null;
  };

  // Prepare data for chart
  const chartData = data.map((item, index) => ({
    ...item,
    shortDate: formatShortDate(item.date),
    isToday: index === data.length - 1
  }));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 md:p-6 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base md:text-lg font-bold text-gray-900 dark:text-white">
            اتجاه المبيعات
          </h2>
          <div className="flex items-center gap-2 text-xs md:text-sm text-gray-600 dark:text-gray-400 mt-1">
            <TrendingUp className="w-3 h-3 md:w-4 md:h-4" />
            <span>آخر 7 أيام</span>
          </div>
        </div>
      </div>

      {/* Summary Stats - Compact for Mobile */}
      <div className="grid grid-cols-2 gap-2 md:gap-4 mb-4">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-3">
          <p className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 mb-0.5">
            إجمالي المبيعات
          </p>
          <p className="text-sm md:text-lg font-bold text-gray-900 dark:text-white break-all">
            {formatCurrency(totalSales)}
          </p>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg p-3">
          <p className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 mb-0.5">
            متوسط اليوم
          </p>
          <p className="text-sm md:text-lg font-bold text-gray-900 dark:text-white break-all">
            {formatCurrency(avgSales)}
          </p>
        </div>
      </div>

      {/* Chart - Responsive Container */}
      <div className="w-full" style={{ direction: 'ltr' }}>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 10, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e5e7eb"
              className="dark:stroke-gray-700"
            />
            <XAxis
              dataKey="shortDate"
              tick={{
                fill: '#6b7280',
                fontSize: 11,
                fontFamily: 'Cairo, sans-serif'
              }}
              tickLine={{ stroke: '#9ca3af' }}
              axisLine={{ stroke: '#d1d5db' }}
            />
            <YAxis
              tick={{
                fill: '#6b7280',
                fontSize: 11
              }}
              tickLine={{ stroke: '#9ca3af' }}
              axisLine={{ stroke: '#d1d5db' }}
              tickFormatter={(value) => {
                if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
                return value;
              }}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
            />
            <Bar
              dataKey="sales"
              radius={[8, 8, 0, 0]}
              maxBarSize={60}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.isToday ? '#3b82f6' : '#60a5fa'}
                  className="hover:opacity-80 transition-opacity"
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Footer Note */}
      <p className="text-[10px] md:text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
        اضغط على الأعمدة لعرض التفاصيل
      </p>
    </div>
  );
};

export default SalesChart;
